import { createApi } from "@reduxjs/toolkit/query/react";
import {
  ApiResponse,
} from "model/response-model";
import {
  AddRole,
  PermissionByIdModel,
  PermissionModel,
  RoleItemModel,
  UpdateRole,
} from "model/roles-permission-model";
import { CreateUserModel, FilterUserModel, ItemModel } from "model/users-model";
import { baseQueryWithReauth } from "./config/interceptor";

// Define a service using a base URL and expected endpoints
export const userRoleApi = createApi({
  reducerPath: "userRoleApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["UserRole"],
  endpoints: (builder) => ({
    addRole: builder.mutation<ApiResponse<AddRole>, AddRole>({
      query: (credentials) => ({
        url: "Roles",
        method: "POST",
        body: credentials,
      }),
    }),
    updateRole: builder.mutation<ApiResponse<AddRole>, UpdateRole>({
      query: (credentials) => ({
        url: "Roles/edit-role",
        method: "PUT",
        body: credentials,
      }),
    }),
    getPermissions: builder.query<ApiResponse<PermissionModel[]>, void>({
      query: () => ({ url: "Roles/permissions" }),
    }),
    getPermissionsById: builder.query<ApiResponse<PermissionByIdModel>, string>(
      {
        query: (roleId) => ({ url: `Roles/${roleId}/permissions` }),
      }
    ),
    getRoles: builder.query<ApiResponse<RoleItemModel>, FilterUserModel>({
      query: (params) => ({
        url: "Roles",
        params: params,
      }),
    }),
    deleteRole: builder.mutation<ApiResponse<string>, string>({
      query: (roleId) => ({
        url: `Roles/${roleId}`, // Assuming roleId is the resource identifier
        method: "DELETE",
      }),
    }),
    createUsers: builder.mutation<ApiResponse<null>, CreateUserModel>({
      query: (credentials) => ({
        url: "Users",
        method: "POST",
        body: credentials,
      }),
    }),
    suspendUsers: builder.mutation<ApiResponse<null>, any>({
      query: (userId) => ({
        url: "Users/suspend",
        method: "POST",
        body: userId,
      }),
    }),
    reactivateUsers: builder.mutation<ApiResponse<null>, any>({
      query: (userId) => ({
        url: "Users/reactivate",
        method: "POST",
        body: userId,
      }),
    }),
    updateUsers: builder.mutation<ApiResponse<null>, any>({
      query: (credentials) => ({
        url: `Users/${credentials?.userId}`,
        method: "PATCH",
        body: credentials,
      }),
    }),
    resetUsersPassword: builder.mutation<ApiResponse<null>, any>({
      query: (credentials) => ({
        url: `Users/${credentials?.userId}/password/reset`,
        method: "PATCH",
        body: credentials,
      }),
    }),

    getUsers: builder.query<ApiResponse<ItemModel>, void>({
      query: () => ({
        url: `Users`,
      }),
    }),
    getUsersWithFilter: builder.query<ApiResponse<ItemModel>, FilterUserModel>({
      query: (filter) => ({
        url: `Users`,
        params: filter,
      }),
    }),
    getSuspendedUsers: builder.query<ApiResponse<ItemModel>, FilterUserModel>({
      query: () => ({
        url: "Users/suspended",
      }),
    }),
    getDeletedUsers: builder.query<ApiResponse<ItemModel>, void>({
      query: () => ({ url: "Users/deleted" }),
    }),
    deleteUser: builder.mutation<ApiResponse<string>, string>({
      query: (userId) => ({
        url: `Users/${userId}`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useAddRoleMutation,
  useUpdateRoleMutation,
  useGetRolesQuery,
  useGetPermissionsQuery,
  useDeleteRoleMutation,
  useGetPermissionsByIdQuery,
  useGetUsersQuery,
  useGetUsersWithFilterQuery,
  useCreateUsersMutation,
  useUpdateUsersMutation,
  useDeleteUserMutation,
  useGetDeletedUsersQuery,
  useGetSuspendedUsersQuery,
  useReactivateUsersMutation,
  useResetUsersPasswordMutation,
  useSuspendUsersMutation,
} = userRoleApi;
